import { BootstrapData, PutReserveInput, UpdateTxInput } from '../api/types';
import { LEGACY_ONE_OFF_KEY, readLegacyOneOffIds } from '../utils/oneOff';
import { LEGACY_RESERVE_KEY, readLegacyReserveStore } from '../utils/reserveState';

/**
 * Одноразовый бэкфилл пер-девайсного состояния (до миграции 0010) на сервер:
 * разметка «разовое» и судьба резервов из localStorage → D1.
 *
 * Правила сохранности:
 *  - ключи localStorage удаляются ТОЛЬКО после того, как все мутации ушли
 *    (синхронно или легли в офлайн-очередь — она их доведёт);
 *  - если сервер уже знает про запись (другое устройство успело раньше) —
 *    не перетираем: сервер выигрывает;
 *  - повторный запуск идемпотентен (upsert по PK, PATCH абсолютом).
 */
const FLAG = 'state_migrated_v1';

interface MigrationDeps {
  data: BootstrapData;
  updateTx: (input: UpdateTxInput) => Promise<{ synced: boolean }>;
  putReserve: (input: PutReserveInput) => Promise<{ synced: boolean }>;
}

export async function migrateDeviceState({ data, updateTx, putReserve }: MigrationDeps): Promise<void> {
  try {
    if (localStorage.getItem(FLAG)) return;
  } catch {
    return; // localStorage недоступен — нечего мигрировать
  }

  let enqueuedAll = true;

  // «разовые»: только id, существующие на сервере и ещё не помеченные
  const oneOff = readLegacyOneOffIds();
  if (oneOff.size > 0) {
    const opById = new Map(data.operations.map((o) => [o.id, o] as const));
    for (const id of oneOff) {
      const op = opById.get(id);
      if (!op || op.is_one_off) continue;
      try {
        await updateTx({ id, is_one_off: true });
      } catch {
        enqueuedAll = false; // бизнес-ошибка — не теряем локальный ключ
      }
    }
  }

  // резервы: released/swept всех месяцев из легаси-стора
  const legacy = readLegacyReserveStore();
  const serverKeys = new Set((data.reserve_state ?? []).map((r) => `${r.month}|${r.category}`));
  const kinds = [
    ['released', legacy.released],
    ['swept', legacy.swept],
  ] as const;
  for (const [state, byMonth] of kinds) {
    for (const [month, cats] of Object.entries(byMonth)) {
      for (const cat of cats) {
        if (serverKeys.has(`${month}|${cat}`)) continue;
        try {
          await putReserve({ month, category: cat, state });
        } catch {
          enqueuedAll = false;
        }
      }
    }
  }

  if (enqueuedAll) {
    try {
      localStorage.setItem(FLAG, '1');
      localStorage.removeItem(LEGACY_ONE_OFF_KEY);
      localStorage.removeItem(LEGACY_RESERVE_KEY);
    } catch {
      /* ignore */
    }
  }
}
